import {
  ElevenLabsVoicePersona,
  VoiceCallBackgroundSound,
  VoiceCallFirstMessageMode,
  VoiceCallIdleMessage,
  VoiceCallLanguage,
  VoiceCallModel,
  VoiceCallOptionMode,
  VoiceProvider,
} from 'flow-schema';

import { api } from 'common';

import { VoiceCallFormFields } from './FormFields';
import { voiceCallBlockDocumentationMarkdown } from './documentation';
import { CodeMirrorLanguageType } from '../../../../Code/types';
import { type BlockConfigProps } from '../../types';
import { defaultCommunicationWindow } from '../shared/CommunicationWindow';

const { DurationUnits, VoiceCallBlockConfigSchema } = api.FlowDefinition;

export const voiceCallNodeConfig: BlockConfigProps<api.FlowDefinition.VoiceCallBlock['config']> = {
  nodeHandles: [{ type: 'target' }, { type: 'source' }],
  defaultLabelPrefix: 'voiceCall',
  defaultConfig: {
    calleePhoneNumber: '',
    callerPhoneNumberId: '',
    firstMessage: 'Hello, this is an automated call from your healthcare provider.',
    firstMessageMode: VoiceCallFirstMessageMode.AssistantWaitsForUser,
    prompt: `## Identity
You are a secure, HIPAA-compliant voice agent and digital assistant for a professional medical center. Maintain a polite, professional and helpful tone at all times. Use simple words. 

## Goal
Your goal is to assist patients schedule an appointment that their doctor recommends. Ensure the conversation addresses this task and does not stray beyond its scope.

## Style
- Be helpful, courteous, and empathetic
- Maintain a professional yet warm tone
- Be concise and clear in your communication, as you are operating in a voice call conversation
- Use simple words: “help” not “assist”, “simple” not “straightforward”
- Your goal is to act efficiently. You must not say filler phrases like: "I see", "Got it", "I understand", "Thank you for confirming", "Let me help you with that", "certainly"
- Ask one question at a time 
- After each instruction, pause and check: “Let me know when you're ready for the next step.” 
- If the caller is silent or seems lost, say: “Would you like me to repeat that, or are you ready to continue?”
- Use, “Take your time,” and “No rush,” throughout
- If interrupted, pause and ask if they want instructions repeated

## Guardrails
- If the patient is having a medical emergency, tell them to seek emergency care by hanging up and calling 9-1-1 or to go to the emergency room
- If the user asks if you're an AI, say yes and reaffirm that you can do anything a human can, without putting them on hold
- For questions outside your scope, transfer people to a senior agent
- You may NEVER provide clinical advice
- NEVER disclose personal information such as patients' last names, dates of birth, or which specific information was incorrect during authentication
- Avoid explaining technical issues or system problems
- If caller becomes angry, off-topic, or requests a person, let them know you will transfer them to a senior agent
- Don't include random non-English words/phrases
`,
    modelName: VoiceCallModel.gpt_4_1,
    voiceProvider: VoiceProvider.ElevenLabs,
    voicemailMessage: undefined,
    language: VoiceCallLanguage.English,
    voicePersona: ElevenLabsVoicePersona.Sarah,
    timeout: {
      value: 45,
      units: DurationUnits.Minutes,
    },
    maxCallDuration: {
      value: 15,
      units: DurationUnits.Minutes,
    },
    tools: [],
    decisionTreeId: undefined,
    metadata: undefined,
    endCallMessage: 'Have a great day!',
    emotionRecognition: VoiceCallOptionMode.Enabled,
    endCallFunction: VoiceCallOptionMode.Enabled,
    startSpeakingSmartEndpointing: VoiceCallOptionMode.Enabled,
    backgroundDenoising: VoiceCallOptionMode.Enabled,
    backgroundSound: VoiceCallBackgroundSound.Off,
    startSpeakingWaitSeconds: 2.0,
    voiceSimilarityBoost: 0.6,
    voiceSpeed: 1.0,
    voiceStability: 0.3,
    idleMessages: [VoiceCallIdleMessage.AreYouStillThere],
    idleMessageMaxSpokenCount: 3,
    idleTimeoutSeconds: 5,
    stopSpeakingNumberOfWords: 5,
    stopSpeakingVoiceSeconds: 0.5,
    stopSpeakingVoiceBackOffSeconds: 0.5,
    modelTemperature: 0.3,
    enableSsmlParsing: false,
    communicationWindow: defaultCommunicationWindow,
  },
  configFields: {
    getLanguageFieldValues: (config) => {
      return {
        [CodeMirrorLanguageType.DoubleBraceNodeFields]: [
          config.calleePhoneNumber,
          config.prompt,
          config.firstMessage,
          config.voicemailMessage,
        ],
      };
    },
    schema: VoiceCallBlockConfigSchema,
    FormFields: VoiceCallFormFields,
  },
  documentationMarkdown: voiceCallBlockDocumentationMarkdown,
};

export const voiceCallV2NodeConfig: BlockConfigProps<
  api.FlowDefinition.VoiceCallV2Block['config']
> = {
  ...voiceCallNodeConfig,
};
